import ScaledTowerDiagram from "./ScaledTowerDiagram";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import Visible from "components/common/Visible";
import * as labels from "config.labels";
import { TOWER_SIR_SUMMARY_DISPLAY } from "flags";
import { Fragment, useMemo } from "react";
import NumberFormat from "react-number-format";
import { connect, useSelector } from "react-redux";
import { useConfigQuery, useFeatureFlag } from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as towerPricingSelectors from "store/selectors/pricing/tower/towerPricingSelectors";
import * as utils from "utils";

const percentFormatted = numbers.dpString(0, { suffix: "%" });

const getLayerMeta = (layer, numberLayers, index, { showSir = false } = {}) => {
  if (layer.attachment === 0) {
    const enDashChar = "\u2013";

    return {
      title: "Retentions",
      description: enDashChar,
      size: numbers.abbreviated(layer.limit),
      isRetention: true,
      gwp: enDashChar,
      rpm: enDashChar,
      rate: enDashChar,
    };
  }

  const sizeLabel =
    showSir && layer.sir
      ? `${utils.shortLayerName(layer)} (SIR: $${numbers.abbreviated(
          layer.sir
        )})`
      : utils.shortLayerName(layer);

  return {
    title: `Layer ${numberLayers - index}`,
    description: `${layer.shares.map((x) => x.carrier).join(", ")}`,
    size: sizeLabel,
    gwp: numbers.abbreviated(layer.grossPremium),
    rpm: numbers.abbreviated(layer.grossRPM),
    rate: isNaN(layer?.grossRPMRelativeToLayerBelow ?? NaN)
      ? ""
      : percentFormatted(layer.grossRPMRelativeToLayerBelow * 100),
  };
};

const getLayerClass = (attachment, isParticipant) => {
  if (attachment === 0) {
    return "layer dim";
  }
  if (isParticipant) {
    return "layer highlight";
  }
  return "layer";
};

const LabelledData = ({ label, data, align, minWidth }) => {
  const style = {
    textAlign: align,
    minWidth: minWidth,
  };

  return !!data ? (
    <Stack style={style}>
      <Typography variant={"overline"}>{label}</Typography>
      <Typography variant={"subtitle2"}>{data}</Typography>
    </Stack>
  ) : (
    <Box style={style} />
  );
};

const TowerDiagramLayer = (
  layer,
  numberLayers,
  index,
  isParticipant,
  { showSir = false } = {}
) => {
  const meta = getLayerMeta(layer, numberLayers, index, { showSir });
  return (
    <Fragment key={`TOWER_${index}`}>
      <div className={getLayerClass(layer.attachment, isParticipant)}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Stack direction={"row"} gap={"0.5rem"}>
            <LabelledData
              label={meta.title}
              data={meta.size}
              minWidth={"7.5rem"}
            />
            <LabelledData label={"Participants"} data={meta.description} />
          </Stack>

          <Stack direction={"row"} flexShrink={"0"} gap={"0.5rem"}>
            <LabelledData
              label={"GWP"}
              data={meta.gwp}
              align={"center"}
              minWidth={"4rem"}
            />
            <LabelledData
              label={"RPM"}
              data={meta.rpm}
              align={"center"}
              minWidth={"4rem"}
            />
            <LabelledData
              label={"Relative Rate"}
              data={meta.rate}
              align={"center"}
              minWidth={"6rem"}
            />
          </Stack>
        </Stack>
      </div>
    </Fragment>
  );
};

const TowerDiagram = ({ tower, pricingLayers, showSir = false }) => {
  return (
    <div className={"tower-diagram"}>
      {tower.map((layer, index) => {
        const numberLayers = tower.length - 1;

        const isParticipant = pricingLayers.some((x) => {
          return numbers.rangesOverlap(
            x.attachment,
            x.attachment + x.limit,
            layer.attachment,
            layer.attachment + layer.limit
          );
        });

        return TowerDiagramLayer(layer, numberLayers, index, isParticipant, {
          showSir,
        });
      })}
    </div>
  );
};

const TowerParticipationRow = ({ layer, revenue }) => {
  return (
    <TableRow>
      <Visible byTag={"summary.print.layers.status"} defaultVisible={true}>
        <TableCell align={"center"}>
          <StatusBadgeV2 status={layer.status} variant={"chip"} />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.ourLimit"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={layer.lineSize}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.partOf"} defaultVisible={true}>
        <TableCell align={"center"}>
          {numbers.abbreviated(layer.limit)}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.excess"} defaultVisible={true}>
        <TableCell align={"center"}>
          {numbers.abbreviated(layer.attachment)}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.sir"} defaultVisible={false}>
        <TableCell align={"right"}>
          {layer.sir != null ? (
            <NumberFormat
              displayType={"text"}
              thousandSeparator
              decimalScale={0}
              prefix={"$"}
              value={layer.sir}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>

      <Visible
        byTag={"summary.print.layers.effectiveAttachment"}
        defaultVisible={false}
      >
        <TableCell align={"center"}>
          {layer.effectiveAttachment != null
            ? numbers.abbreviated(layer.effectiveAttachment)
            : "–"}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.premium"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={layer.grossPremium}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.rpm"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={layer.grossPPM}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.ourGross"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={layer.shareOfPremium}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.ourNet"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={((100 - layer.tac) / 100) * layer.shareOfPremium}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.tac"} defaultVisible={true}>
        <TableCell align={"center"}>
          {layer.tac}
          {"%"}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.tp"} defaultVisible={true}>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            decimalScale={0}
            suffix={"%"}
            value={layer.uwSelectedMeasure?.targetPrice}
            className={
              (layer.uwSelectedMeasure?.targetPrice ?? 100) - 100 < 0
                ? "table-cell-bad-deal"
                : "table-cell-good-deal"
            }
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.gnulr"} defaultVisible={true}>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            decimalScale={2}
            suffix={"%"}
            value={layer.uwSelectedMeasure?.technicalRatio}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.brokerage"} defaultVisible={false}>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={2}
            suffix={"%"}
            value={layer.brokerage}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.slipFee"} defaultVisible={false}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            suffix={"%"}
            value={layer.slipFee}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.gelr"} defaultVisible={false}>
        <TableCell align={"center"}>
          {layer.grossEstLossRatio != null ? (
            <NumberFormat
              displayType={"text"}
              decimalScale={2}
              suffix={"%"}
              value={layer.grossEstLossRatio}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.rarc"} defaultVisible={false}>
        <TableCell align={"center"}>
          {layer.riskAdjustedReturnOnCapital != null ? (
            <NumberFormat
              displayType={"text"}
              decimalScale={2}
              suffix={"%"}
              value={layer.riskAdjustedReturnOnCapital}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.signedLine"} defaultVisible={false}>
        <TableCell align={"center"}>
          {layer.shareOfLine != null ? (
            <NumberFormat
              displayType={"text"}
              decimalScale={6}
              suffix={"%"}
              value={layer.shareOfLine}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>

      <Visible
        byTag={"summary.print.layers.puniPremium"}
        defaultVisible={false}
      >
        <TableCell align={"right"}>
          {layer.shareOfPuniWrap != null ? (
            <NumberFormat
              displayType={"text"}
              thousandSeparator
              decimalScale={0}
              prefix={"$"}
              value={layer.shareOfPuniWrap}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>

      <Visible
        byTag={"summary.print.layers.premiumExPuni"}
        defaultVisible={false}
      >
        <TableCell align={"right"}>
          {layer.shareOfPremiumExPuniWrap != null ? (
            <NumberFormat
              displayType={"text"}
              thousandSeparator
              decimalScale={0}
              prefix={"$"}
              value={layer.shareOfPremiumExPuniWrap}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.revenue"} defaultVisible={false}>
        <TableCell align={"right"}>
          {revenue != null ? (
            <NumberFormat
              displayType={"text"}
              thousandSeparator
              decimalScale={0}
              prefix={"$"}
              value={revenue}
            />
          ) : (
            "–"
          )}
        </TableCell>
      </Visible>
    </TableRow>
  );
};

const TowerParticipationSummaryRow = ({ pricingLayers }) => {
  const limitReducer = (acc, current) =>
    acc + (current.bound ? current.lineSize : 0);
  const grossReducer = (acc, current) =>
    acc + (current.bound ? current.shareOfPremium : 0);
  const netReducer = (acc, current) =>
    acc +
    (current.bound ? (1 - current.tac / 100) * current.shareOfPremium : 0);
  const costReducer = (acc, current) =>
    acc + (current.bound ? (current.tac / 100) * current.shareOfPremium : 0);
  const totalSlipFee = pricingLayers.reduce(
    (acc, layer) =>
      acc + (layer.bound ? (layer.slipFee / 100) * layer.shareOfPremium : 0),
    0
  );
  const averageBrokerage = pricingLayers.reduce(
    (acc, layer) =>
      acc + (layer.bound ? (layer.brokerage / 100) * layer.shareOfPremium : 0),
    0
  );
  return (
    <TableRow>
      <Visible byTag={"summary.print.layers.status"} defaultVisible={true}>
        <TableCell align={"center"}>
          <b>{"Total Bound"}</b>
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.ourLimit"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={pricingLayers.reduce(limitReducer, 0)}
            style={{ fontWeight: 600 }}
          />
        </TableCell>
      </Visible>

      {["partOf", "excess"].map((tag) => (
        <Visible
          key={tag}
          byTag={`summary.print.layers.${tag}`}
          defaultVisible={true}
        >
          <TableCell></TableCell>
        </Visible>
      ))}

      {["sir", "effectiveAttachment"].map((tag) => (
        <Visible
          key={tag}
          byTag={`summary.print.layers.${tag}`}
          defaultVisible={false}
        >
          <TableCell></TableCell>
        </Visible>
      ))}

      {["premium", "rpm"].map((tag) => (
        <Visible
          key={tag}
          byTag={`summary.print.layers.${tag}`}
          defaultVisible={true}
        >
          <TableCell></TableCell>
        </Visible>
      ))}

      <Visible byTag={"summary.print.layers.ourGross"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={pricingLayers.reduce(grossReducer, 0)}
            style={{ fontWeight: 600 }}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.ourNet"} defaultVisible={true}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={pricingLayers.reduce(netReducer, 0)}
            style={{ fontWeight: 600 }}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.tac"} defaultVisible={true}>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={0}
            prefix={"$"}
            value={pricingLayers.reduce(costReducer, 0)}
            style={{ fontWeight: 600 }}
          />
        </TableCell>
      </Visible>

      {["tp", "gnulr"].map((tag) => (
        <Visible
          key={tag}
          byTag={`summary.print.layers.${tag}`}
          defaultVisible={true}
        >
          <TableCell></TableCell>
        </Visible>
      ))}

      <Visible byTag={"summary.print.layers.brokerage"} defaultVisible={false}>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            decimalScale={2}
            thousandSeparator
            prefix={"$"}
            value={averageBrokerage}
            style={{ fontWeight: 600 }}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"summary.print.layers.slipFee"} defaultVisible={false}>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator
            decimalScale={2}
            prefix={"$"}
            value={totalSlipFee}
            style={{ fontWeight: 600 }}
          />
        </TableCell>
      </Visible>

      {[
        "gelr",
        "rarc",
        "signedLine",
        "puniPremium",
        "premiumExPuni",
        "revenue",
      ].map((tag) => (
        <Visible
          key={tag}
          byTag={`summary.print.layers.${tag}`}
          defaultVisible={false}
        >
          <TableCell />
        </Visible>
      ))}
    </TableRow>
  );
};

const TowerParticipation = ({
  pricingLayers,
  revenueExposureKey = "REVENUE",
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const latestExposures = useSelector(exposureSelectors.getLatestExposures);
  const revenueExposure = latestExposures.find(
    (exposure) => exposure.key === revenueExposureKey
  );
  const revenue = revenueExposure?.value ?? null;

  return (
    <TableContainer>
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <Visible
              byTag={"summary.print.layers.status"}
              defaultVisible={true}
            >
              <TableCell align={"center"}>{"Status"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.ourLimit"}
              defaultVisible={true}
            >
              <TableCell align={"right"}>
                {translateLabel(labels.PRINT_TOWER_PRICING_AGENCY_LIMIT, {
                  default: "Our Limit",
                })}
              </TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.partOf"}
              defaultVisible={true}
            >
              <TableCell align={"center"}>
                {translateLabel(labels.PRINT_TOWER_PRICING_WHOLE_LAYER_LIMIT, {
                  default: "Part Of",
                })}
              </TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.excess"}
              defaultVisible={true}
            >
              <TableCell align={"center"}>
                {translateLabel(labels.PRINT_TOWER_PRICING_ATTACHMENT_POINT, {
                  default: "Excess",
                })}
              </TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.sir"} defaultVisible={false}>
              <TableCell align={"right"}>{"SIR"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.effectiveAttachment"}
              defaultVisible={false}
            >
              <TableCell align={"center"}>{"Eff. Attachment"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.premium"}
              defaultVisible={true}
            >
              <TableCell align={"right"}>{"Premium"}</TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.rpm"} defaultVisible={true}>
              <TableCell align={"right"}>{"RPM"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.ourGross"}
              defaultVisible={true}
            >
              <TableCell align={"right"}>
                {translateLabel(labels.PRINT_TOWER_PRICING_AGENCY_PREMIUM, {
                  default: "Our Gross",
                })}
              </TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.ourNet"}
              defaultVisible={true}
            >
              <TableCell align={"right"}>{"Our Net"}</TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.tac"} defaultVisible={true}>
              <TableCell align={"center"}>{"TAC"}</TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.tp"} defaultVisible={true}>
              <TableCell align={"center"}>{"TP"}</TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.gnulr"} defaultVisible={true}>
              <TableCell align={"center"}>{"GNULR"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.brokerage"}
              defaultVisible={false}
            >
              <TableCell align={"center"}>{"Brokerage"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.slipFee"}
              defaultVisible={false}
            >
              <TableCell align={"center"}>{"Slip Fee"}</TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.gelr"} defaultVisible={false}>
              <TableCell align={"center"}>{"GELR"}</TableCell>
            </Visible>

            <Visible byTag={"summary.print.layers.rarc"} defaultVisible={false}>
              <TableCell align={"center"}>{"RARC"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.signedLine"}
              defaultVisible={false}
            >
              <TableCell align={"center"}>{"Signed Line %"}</TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.puniPremium"}
              defaultVisible={false}
            >
              <TableCell align={"right"}>
                {translateLabel(labels.PRINT_TOWER_PRICING_PUNI_PREMIUM, {
                  default: "Puni Premium",
                })}
              </TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.premiumExPuni"}
              defaultVisible={false}
            >
              <TableCell align={"right"}>
                {translateLabel(labels.PRINT_TOWER_PRICING_PREMIUM_EX_PUNI, {
                  default: "Premium ex Puni",
                })}
              </TableCell>
            </Visible>

            <Visible
              byTag={"summary.print.layers.revenue"}
              defaultVisible={false}
            >
              <TableCell align={"right"}>{"Revenue"}</TableCell>
            </Visible>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...pricingLayers]
            .sort((a, b) => b.attachment - a.attachment)
            .map((layer, index) => (
              <TowerParticipationRow
                layer={layer}
                revenue={revenue}
                key={`TOWER_TABLE_${index}`}
              />
            ))}
          <TowerParticipationSummaryRow pricingLayers={pricingLayers} />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const countLayers = (pricingLayers) => {
  const numberLayers = (pricingLayers ?? []).length;
  if (numberLayers === 1 && pricingLayers[0].lineSize == null) {
    return 0;
  }
  return numberLayers;
};

const Tower = ({
  tower,
  pricingLayers,
  showPricingResults,
  revenueExposureKey = "REVENUE",
}) => {
  const hasTower = (tower ?? []).length > 0;
  const hasLayers = countLayers(pricingLayers) > 0;
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const showSir = useFeatureFlag(TOWER_SIR_SUMMARY_DISPLAY);

  const retentions =
    useSelector(claimsSelectors.selectSelfInsuredRetentions) || [];
  const lossCategories = useSelector(
    (state) => state?.temp?.exposureMetrics?.lossCategories || []
  );
  const lossTypeMap = useMemo(() => {
    return Object.fromEntries(lossCategories.map((c) => [c.key, c.display]));
  }, [lossCategories]);
  const { data: retentionsConfig } = useConfigQuery("retentions");
  const coverageMap = useMemo(() => {
    const entries = (
      retentionsConfig?.retentionCoverageCategory || []
    ).map((c) => [c.key, c.display]);
    return Object.fromEntries(entries);
  }, [retentionsConfig?.retentionCoverageCategory]);
  const retentionMaps = useMemo(() => ({ lossTypeMap, coverageMap }), [
    lossTypeMap,
    coverageMap,
  ]);

  return (
    <>
      {hasTower && (
        <Visible byTag={"<Print>.tower.diagram"} defaultVisible>
          <Stack className={"print-section print-no-break"}>
            <span className={"print-label"} style={{ marginBottom: "8px" }}>
              {translateLabel(labels.PRINT_TOWER, {
                default: "Tower",
              })}
            </span>
            <ScaledTowerDiagram
              tower={tower}
              showSir={showSir}
              retentions={retentions}
              retentionMaps={retentionMaps}
            />
          </Stack>
        </Visible>
      )}

      {hasTower && (
        <Visible byTag={"<Print>.tower.tower"} defaultVisible>
          <Stack className={"print-section"}>
            <span className={"print-label"} style={{ marginBottom: "8px" }}>
              {translateLabel(labels.PRINT_TOWER, {
                default: "Tower",
              })}
            </span>
            <TowerDiagram
              tower={tower}
              pricingLayers={pricingLayers}
              showSir={showSir}
            />
          </Stack>
        </Visible>
      )}

      {hasLayers && showPricingResults && (
        <Visible byTag={"<Print>.tower.pricing"} defaultVisible>
          <Stack className={"print-section"}>
            <span className={"print-label"}>{"Pricing Results"}</span>
            <TowerParticipation
              pricingLayers={pricingLayers}
              revenueExposureKey={revenueExposureKey}
            />
          </Stack>
        </Visible>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    tower: towerPricingSelectors.getInvertedTower(state),
    pricingLayers: pricingSelectors.getTower(state),
    showPricingResults: !(
      state.print.elementVisibility.hidePricingResults ?? false
    ),
  };
};

export default connect(mapStateToProps)(Tower);
